import { useId, useState } from 'react';
import { helpTopicContent, t, type HelpTopic, type StringKey } from '@bms2/shared';
import { Icon } from '../Icon';
import { ModalShell } from './ModalShell';
import styles from './modal.module.css';

/**
 * Help and About (piece 5, decision 0443). An information control beside a
 * page's title opens one explanation of the concept that page is about.
 *
 * 🔨 The explanation is drawn in the Modal shell and never in a second overlay
 * mechanism. A tooltip cannot hold a comparison table, is unreachable by a
 * finger, and vanishes while it is being read; piece 3 already refuses a
 * transient popup for a field's own error, for the same reason.
 *
 * 🔨 A help modal explains and never acts. It carries no field, no write and no
 * consequence, so its Footer is one Close control rather than the Primary,
 * Cancel and destructive slot an acting modal's Footer holds. That is also why
 * a comparison table inside one does not make it the page decision 0409 would
 * ask for: the rule of thumb counts the inputs on an act that writes, and this
 * writes nothing.
 *
 * 🔨 Every word comes from the string lookup through the topic registry in
 * `packages/shared`, so a concept reads the same on every screen that explains
 * it (decision 0013).
 */
export const helpModalDocumentedStates = ['definition', 'comparison'] as const;

/**
 * A comparison table a page computes from a list the shared package holds, its
 * cells already read through the one lookup. 🔨 It is for a topic whose rows ARE
 * a list the application keeps — the print column sets (T194; decision 0657) —
 * where a table of typed keys would be a second copy of that list to drift.
 */
export interface HelpTableText {
  columns: readonly string[];
  rows: readonly (readonly string[])[];
}

/**
 * 🔒 `detail` is the one line of a topic that changes from record to record, drawn
 * bold between the topic's lead and its notes: the Deal Value help's own source
 * (decision 1219). The topic itself stays the registry's, the same everywhere.
 */
export function HelpModal({ topic, open, onClose, openerId, table, detail = null }: { topic: HelpTopic; open: boolean; onClose: () => void; openerId?: string | null; table?: HelpTableText; detail?: string | null }) {
  const content = helpTopicContent(topic);
  const drawn: HelpTableText | null = table ?? (content.table ? { columns: content.table.columns.map((key) => t(key)), rows: content.table.rows.map((row) => row.map((key) => t(key))) } : null);

  return (
    <ModalShell
      open={open}
      title={t(content.title)}
      subtitle={t('help.subtitle')}
      onCancel={onClose}
      dataModal={`help-${topic}`}
      width={drawn ? 'wide' : 'regular'}
      openerId={openerId ?? null}
      footer={
        <button type="button" className={styles.cancel} onClick={onClose} data-modal-exit="cancel">
          {t('help.close')}
        </button>
      }
    >
      {content.lead.map((key) => (
        <p key={key} className={styles.helpParagraph}>
          {t(key)}
        </p>
      ))}

      {drawn ? (
        // 🔨 The table scrolls sideways in its own box rather than widening the
        // Body: the Body's column shrinks below what stands in it (piece 5), so
        // a three-column comparison on a phone must not set a floor under it.
        <div className={styles.helpTableScroll} data-help-table="true">
          <table className={styles.helpTable}>
            <thead>
              <tr>
                {drawn.columns.map((text, column) => (
                  <th key={column} scope="col" className={styles.helpTableHead}>
                    {text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drawn.rows.map((row, at) => (
                <tr key={at}>
                  {row.map((text, column) =>
                    column === 0 ? (
                      <th key={column} scope="row" className={styles.helpTableRowHead}>
                        {text}
                      </th>
                    ) : (
                      <td key={column} className={styles.helpTableCell}>
                        {text}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {detail ? <p className={styles.helpDetail} data-help-detail={topic}>{detail}</p> : null}

      {content.notes.map((key) => (
        <p key={key} className={styles.helpNote}>
          {t(key)}
        </p>
      ))}
    </ModalShell>
  );
}

/**
 * The control that opens it: an information glyph beside a page's title, in
 * the Page Module's own controls slot. It carries its own open state, so a page
 * opts in with one element and holds nothing.
 *
 * 🔨 It is a labelled button and never a bare glyph: a screen reader that
 * reaches it has to be told what it opens, and the label is what a person
 * hovering reads too.
 */
export function HelpButton({ topic, table, detail = null }: { topic: HelpTopic; table?: HelpTableText; detail?: string | null }) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <>
      <button
        type="button"
        id={id}
        className={styles.helpButton}
        onClick={() => setOpen(true)}
        aria-label={t('help.open')}
        title={t('help.open')}
        data-help-open={topic}
      >
        <Icon meaning="help" size="inline" />
      </button>
      {/* 🔨 Mounted only while it is open, so it reads its topic afresh each time
          rather than keeping the first one it was rendered with (piece 5). */}
      {open ? <HelpModal topic={topic} open onClose={() => setOpen(false)} openerId={id} detail={detail} {...(table ? { table } : {})} /> : null}
    </>
  );
}

/**
 * 🔒 Several topics read together in one help modal, each under its own title,
 * in the order given (T252; decision 0724). The Deal's Intent card opens its four
 * rows' topics this way from the one control in its head: a topic per row, and
 * one place to read them side by side. 🔨 Each topic is still the registry's own,
 * so a row reads the same wherever else it is explained.
 */
export function HelpTopicsModal({ titleKey, topics, open, onClose, openerId }: { titleKey: StringKey; topics: readonly HelpTopic[]; open: boolean; onClose: () => void; openerId?: string | null }) {
  return (
    <ModalShell
      open={open}
      title={t(titleKey)}
      subtitle={t('help.subtitle')}
      onCancel={onClose}
      dataModal={`help-${topics.join('-')}`}
      width="regular"
      openerId={openerId ?? null}
      footer={
        <button type="button" className={styles.cancel} onClick={onClose} data-modal-exit="cancel">
          {t('help.close')}
        </button>
      }
    >
      {topics.map((topic) => {
        const content = helpTopicContent(topic);
        return (
          <section key={topic} className={styles.helpSection} data-help-topic={topic}>
            <h3 className={styles.helpHeading}>{t(content.title)}</h3>
            {content.lead.map((key) => <p key={key} className={styles.helpParagraph}>{t(key)}</p>)}
            {content.notes.map((key) => <p key={key} className={styles.helpNote}>{t(key)}</p>)}
          </section>
        );
      })}
    </ModalShell>
  );
}

/**
 * The control that opens several topics together; it holds its own open state,
 * as `HelpButton` does. 🔒 A control opening a whole page's guide carries its
 * words beside the glyph (T435; decision 1103), the Import page's *How this
 * works* the first; a concept's help stays the glyph alone.
 */
export function HelpTopicsButton({ titleKey, topics, labelKey }: { titleKey: StringKey; topics: readonly HelpTopic[]; labelKey?: StringKey }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const label = labelKey ? t(labelKey) : t('help.open');
  return (
    <>
      <button type="button" id={id} className={labelKey ? `${styles.helpButton} ${styles.helpButtonLabelled}` : styles.helpButton} onClick={() => setOpen(true)} aria-label={label} title={label} data-help-open={topics.join(' ')}>
        <Icon meaning="help" size="inline" />
        {labelKey ? <span className={styles.helpButtonText}>{label}</span> : null}
      </button>
      {open ? <HelpTopicsModal titleKey={titleKey} topics={topics} open onClose={() => setOpen(false)} openerId={id} /> : null}
    </>
  );
}
